import React, { useState, useRef } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const VSMCapture = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const fileInputRef = useRef(null);

  const T = {
    title:           { ar: '📸 تنفيذ مهمة بصورة اللوحة', en: '📸 Capture Plate Photo' },
    subtitle:        { ar: 'صفحة اختبار الـ OCR ومطابقة اللوحة', en: 'OCR & plate matching test page' },

    selectPhoto:     { ar: 'اختر صورة اللوحة', en: 'Select Plate Photo' },
    chooseFile:      { ar: '📁 اختيار ملف', en: '📁 Choose File' },
    takePhoto:       { ar: '📷 التقاط صورة', en: '📷 Take Photo' },
    upload:          { ar: '⬆️ رفع وتحليل', en: '⬆️ Upload & Analyze' },
    uploading:       { ar: 'جاري التحليل...', en: 'Analyzing...' },
    reset:           { ar: '🔄 إعادة', en: '🔄 Reset' },

    ocrResult:       { ar: 'نتيجة قراءة اللوحة', en: 'OCR Result' },
    rawOcr:          { ar: 'النص المستخرج', en: 'Extracted Text' },
    candidates:      { ar: 'احتمالات اللوحة', en: 'Plate Candidates' },
    normalized:      { ar: 'الصيغة الموحدة', en: 'Normalized' },

    matchedTask:     { ar: 'المهمة المطابقة', en: 'Matched Task' },
    matchExact:      { ar: '✅ تطابق تام', en: '✅ Exact Match' },
    matchFuzzy:      { ar: '⚠️ تطابق تقريبي', en: '⚠️ Approximate Match' },
    matchNone:       { ar: '❌ لا يوجد تطابق', en: '❌ No Match' },
    matchNoSched:    { ar: '📭 لا توجد مهام لليوم', en: '📭 No tasks today' },
    matchNoText:     { ar: '🔍 لم يتم استخراج نص', en: '🔍 No text detected' },

    plate:           { ar: 'اللوحة', en: 'Plate' },
    client:          { ar: 'العميل', en: 'Client' },
    service:         { ar: 'الخدمة', en: 'Service' },
    time:            { ar: 'الوقت', en: 'Time' },
    distance:        { ar: 'فرق الأحرف', en: 'Char Distance' },

    topMatches:      { ar: 'أقرب التطابقات', en: 'Closest Matches' },
    selectManually:  { ar: 'اختار يدوياً', en: 'Select Manually' },

    confirm:         { ar: '✅ تأكيد التنفيذ', en: '✅ Confirm Completion' },
    confirming:      { ar: 'جاري التأكيد...', en: 'Confirming...' },
    confirmed:       { ar: '✅ تم التنفيذ بنجاح', en: '✅ Successfully completed' },
    confirmAgain:    { ar: 'تنفيذ مهمة جديدة', en: 'New Capture' },
    notes:           { ar: 'ملاحظات (اختياري)', en: 'Notes (optional)' },

    error:           { ar: 'خطأ', en: 'Error' },
    success:         { ar: 'نجاح', en: 'Success' },
    waitingPhoto:    { ar: 'اختر صورة لبدء التحليل', en: 'Pick a photo to start' },

    whatsappWillSend:{ ar: '📱 سيتم إشعار العميل عبر واتساب قريباً', en: '📱 Customer will be notified via WhatsApp soon' },
    whatsappNoPhone: { ar: '📵 العميل ليس لديه رقم واتساب مسجل', en: '📵 Customer has no WhatsApp registered' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  // ===== State =====
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [confirming, setConfirming] = useState(false);
  const [completed, setCompleted] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [notes, setNotes] = useState('');

  // ===== File handling =====
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setError(null);
    setCompleted(null);
    setSelectedScheduleId(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('plate_photo', file);

    try {
      const res = await fetch(`${API}/completions/capture`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        // Auto-select if there's a clear match
        if (data.matched_schedule) {
          setSelectedScheduleId(data.matched_schedule.schedule_id);
        }
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('[VSM] Upload error:', err);
      setError(err.message || 'Network error');
    }
    setUploading(false);
  };

  const handleConfirm = async () => {
    if (!selectedScheduleId || !result?.photo_path) return;
    setConfirming(true);

    const token = localStorage.getItem('token');
    const payload = {
      schedule_id: selectedScheduleId,
      photo_path: result.photo_path,
      ocr_raw: result.matched_schedule?.ocr_raw || result.ocr_candidates?.[0] || null,
      ocr_normalized: result.ocr_normalized || null,
      match_method: result.matched_schedule?.match_method || 'manual',
      notes: notes || null
    };

    try {
      const res = await fetch(`${API}/completions/confirm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setCompleted(data.data);
      } else {
        alert(data.error || 'Confirm failed');
      }
    } catch (err) {
      alert(err.message || 'Network error');
    }
    setConfirming(false);
  };

  const handleReset = () => {
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    setError(null);
    setCompleted(null);
    setSelectedScheduleId(null);
    setNotes('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ===== Styles =====
  const cardStyle = {
    background: 'rgba(15,23,42,0.5)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '14px',
    border: '1px solid rgba(255,255,255,0.08)'
  };

  const buttonPrimary = {
    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '10px',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const buttonSecondary = {
    background: 'rgba(100,116,139,0.2)',
    color: '#cbd5e1',
    border: '1px solid rgba(100,116,139,0.3)',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer'
  };

  // Render plate visual
  const PlateVisual = ({ display, lettersEn, lettersAr, numbers }) => {
    if (!lettersEn && !display) return null;
    return (
      <div style={{
        display: 'inline-block',
        background: 'linear-gradient(180deg, #fff, #e5e5e5)',
        border: '3px solid #1a1a2e',
        borderRadius: '6px',
        padding: '8px 16px',
        fontFamily: 'monospace',
        fontWeight: '900',
        color: '#1a1a2e',
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        minWidth: '160px'
      }} dir="ltr">
        {lettersEn ? (
          <>
            <div style={{ fontSize: '14px' }}>{lettersEn} / {lettersAr}</div>
            <div style={{ fontSize: '24px', marginTop: '2px' }}>{numbers}</div>
          </>
        ) : (
          <div style={{ fontSize: '20px' }}>{display}</div>
        )}
      </div>
    );
  };

  // ===== Render =====
  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '900px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
            {tr('title')}
          </h1>
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>
            {tr('subtitle')}
          </p>
        </div>

        {/* Success state */}
        {completed && (
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(34,197,94,0.1))',
            borderColor: 'rgba(16,185,129,0.4)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '12px' }}>✅</div>
            <h2 style={{ color: '#6ee7b7', margin: '0 0 8px', fontSize: '20px' }}>
              {tr('confirmed')}
            </h2>
            <div style={{ fontSize: '13px', color: '#cbd5e1', marginBottom: '14px' }}>
              <div style={{ marginBottom: '6px' }}>
                <strong>{tr('plate')}:</strong> {completed.plate_display}
              </div>
              <div style={{ marginBottom: '6px' }}>
                <strong>{tr('client')}:</strong> {completed.client_name}
              </div>
              <div style={{
                marginTop: '12px',
                padding: '10px',
                background: completed.whatsapp_will_send ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                borderRadius: '8px',
                fontSize: '12px',
                color: completed.whatsapp_will_send ? '#6ee7b7' : '#fcd34d'
              }}>
                {completed.whatsapp_will_send ? tr('whatsappWillSend') : tr('whatsappNoPhone')}
              </div>
            </div>
            <button onClick={handleReset} style={buttonPrimary}>
              {tr('confirmAgain')}
            </button>
          </div>
        )}

        {/* Photo selection */}
        {!completed && (
          <div style={cardStyle}>
            <h3 style={{ fontSize: '14px', color: '#818cf8', margin: '0 0 12px', fontWeight: '700' }}>
              📷 {tr('selectPhoto')}
            </h3>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {!previewUrl ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed rgba(99,102,241,0.4)',
                  borderRadius: '12px',
                  padding: '50px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: 'rgba(99,102,241,0.05)',
                  transition: 'all 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
              >
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>📷</div>
                <div style={{ color: '#a5b4fc', fontWeight: '700', fontSize: '15px' }}>
                  {tr('chooseFile')}
                </div>
                <div style={{ color: '#64748b', fontSize: '11px', marginTop: '6px' }}>
                  {tr('waitingPhoto')}
                </div>
              </div>
            ) : (
              <div>
                <div style={{
                  background: '#0a0e1a', borderRadius: '12px',
                  padding: '8px', marginBottom: '12px', textAlign: 'center'
                }}>
                  <img
                    src={previewUrl}
                    alt="Plate preview"
                    style={{
                      maxWidth: '100%', maxHeight: '320px',
                      borderRadius: '8px',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !!result}
                    style={{
                      ...buttonPrimary,
                      opacity: (uploading || result) ? 0.5 : 1,
                      cursor: (uploading || result) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploading ? tr('uploading') : tr('upload')}
                  </button>
                  <button onClick={handleReset} style={buttonSecondary}>
                    {tr('reset')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            ...cardStyle,
            background: 'rgba(239,68,68,0.1)',
            borderColor: 'rgba(239,68,68,0.3)'
          }}>
            <strong style={{ color: '#fca5a5' }}>❌ {tr('error')}: </strong>
            <span style={{ color: '#fecaca' }}>{error}</span>
          </div>
        )}

        {/* OCR result */}
        {result && !completed && (
          <>
            {/* OCR raw output */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: '14px', color: '#818cf8', margin: '0 0 12px', fontWeight: '700' }}>
                🔍 {tr('ocrResult')}
              </h3>

              {result.ocr_candidates && result.ocr_candidates.length > 0 ? (
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                    {tr('candidates')}:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                    {result.ocr_candidates.slice(0, 5).map((c, idx) => (
                      <span key={idx} style={{
                        background: idx === 0 ? 'rgba(99,102,241,0.2)' : 'rgba(100,116,139,0.15)',
                        color: idx === 0 ? '#a5b4fc' : '#cbd5e1',
                        padding: '4px 10px', borderRadius: '6px',
                        fontSize: '12px', fontFamily: 'monospace', fontWeight: '600'
                      }}>
                        {c}
                      </span>
                    ))}
                  </div>
                  {result.ocr_normalized && (
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                        {tr('normalized')}:
                      </div>
                      <span style={{
                        background: 'rgba(16,185,129,0.15)',
                        color: '#6ee7b7',
                        padding: '5px 14px', borderRadius: '8px',
                        fontSize: '14px', fontFamily: 'monospace', fontWeight: '700'
                      }} dir="ltr">
                        {result.ocr_normalized}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#fca5a5', fontSize: '13px' }}>
                  {tr('matchNoText')}
                </div>
              )}
            </div>

            {/* Match result */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: '14px', color: '#818cf8', margin: '0 0 12px', fontWeight: '700' }}>
                🎯 {tr('matchedTask')}
              </h3>

              {/* Match badge */}
              {result.match_type === 'exact' && (
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(16,185,129,0.2)', color: '#6ee7b7',
                  padding: '5px 12px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: '700', marginBottom: '12px'
                }}>
                  {tr('matchExact')}
                </div>
              )}
              {result.match_type === 'fuzzy' && (
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(245,158,11,0.2)', color: '#fcd34d',
                  padding: '5px 12px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: '700', marginBottom: '12px'
                }}>
                  {tr('matchFuzzy')}
                </div>
              )}
              {(result.match_type === 'no_match' || result.match_type === 'no_text' || result.match_type === 'no_schedule') && (
                <div style={{
                  display: 'inline-block',
                  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                  padding: '5px 12px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: '700', marginBottom: '12px'
                }}>
                  {result.match_type === 'no_text' ? tr('matchNoText')
                    : result.match_type === 'no_schedule' ? tr('matchNoSched')
                    : tr('matchNone')}
                </div>
              )}

              {/* Matched task details */}
              {result.matched_schedule && (
                <div style={{
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '10px', padding: '14px',
                  marginTop: '8px'
                }}>
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <PlateVisual
                      lettersEn={result.matched_schedule.plate_letters_en}
                      lettersAr={result.matched_schedule.plate_letters_ar}
                      numbers={result.matched_schedule.plate_numbers}
                    />
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' }}>
                        {isRTL ? result.matched_schedule.client_name_ar : result.matched_schedule.client_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>
                        🧰 {isRTL ? result.matched_schedule.service_name_ar_snapshot : result.matched_schedule.service_name_snapshot}
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }} dir="ltr">
                        ⏰ {result.matched_schedule.scheduled_time?.slice(0, 5) || '—'}
                      </div>
                      {result.matched_schedule.match_distance > 0 && (
                        <div style={{ fontSize: '11px', color: '#fcd34d', marginTop: '6px' }}>
                          {tr('distance')}: {result.matched_schedule.match_distance}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Top matches for manual selection */}
              {result.top_matches && result.top_matches.length > 0 && result.match_type !== 'exact' && (
                <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600' }}>
                    {tr('topMatches')}:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {result.top_matches.map(m => (
                      <button
                        key={m.schedule_id}
                        onClick={() => setSelectedScheduleId(m.schedule_id)}
                        style={{
                          background: selectedScheduleId === m.schedule_id ? 'rgba(99,102,241,0.25)' : 'rgba(15,23,42,0.5)',
                          color: selectedScheduleId === m.schedule_id ? '#a5b4fc' : '#94a3b8',
                          border: selectedScheduleId === m.schedule_id ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.1)',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '11px', fontFamily: 'monospace',
                          cursor: 'pointer', fontWeight: '600'
                        }} dir="ltr"
                      >
                        {m.plate_display} {m.distance > 0 && `(±${m.distance})`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm form */}
            {selectedScheduleId && (
              <div style={cardStyle}>
                <h3 style={{ fontSize: '14px', color: '#818cf8', margin: '0 0 12px', fontWeight: '700' }}>
                  ✅ تأكيد المهمة
                </h3>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                    {tr('notes')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    style={{
                      width: '100%', padding: '10px', borderRadius: '8px',
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: '#1a2038', color: '#f9fafb',
                      fontSize: '13px', resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  style={{
                    ...buttonPrimary,
                    background: 'linear-gradient(135deg, #10b981, #34d399)',
                    width: '100%',
                    opacity: confirming ? 0.5 : 1,
                    cursor: confirming ? 'not-allowed' : 'pointer'
                  }}
                >
                  {confirming ? tr('confirming') : tr('confirm')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default VSMCapture;
